import { useState } from 'react'
import type { Rec } from '../../shared/schema'
import { quickUpdate, useRecords } from '../lib/api'
import { useEditor } from '../lib/useOpen'
import { fmtDayLong, fmtTime, localToIso, MESES, mapsLink, today, waNumber, ymd } from '../lib/format'
import { addDays, CalNav, DaysColumns, monthLabel, MonthGrid, startOfWeek, useCursor } from '../components/Boards'
import { RecordForm } from '../components/RecordForm'
import { Avatar, Badge, Button, copyText, ErrorNote, Icon, PageHeader, Segmented, Skeleton, useToast } from '../components/ui'

type View = 'dia' | 'semana' | 'mes'

export function Agenda() {
  const { data, loading, error, refresh } = useRecords('visitas')
  const clientes = useRecords('clientes').data
  const props = useRecords('propiedades').data
  const ed = useEditor(data)
  const toast = useToast()
  const [view, setView] = useState<View>('semana')
  const [socia, setSocia] = useState('')
  const { cursor, setCursor } = useCursor()

  const items = data.filter(v => !socia || v.socia === socia)
  const cli = (id: string) => clientes.find(c => c.id === id)
  const prop = (id: string) => props.find(p => p.id === id)
  const dayOf = (v: Rec) => (v.inicio ? ymd(new Date(v.inicio)) : undefined)

  const moveTo = async (v: Rec, day: string) => {
    const d = new Date(v.inicio)
    const inicio = localToIso(`${day}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`)
    try { await quickUpdate('visitas', v, { inicio }); toast(`Reprogramada para el ${fmtDayLong(day)}`) } catch (e: any) { toast(e.message, 'error') }
  }
  const newAt = (day: string, hour = 10) => ed.create({ tipo: 'Visita', estado: 'Programada', duracion: 60, inicio: localToIso(`${day}T${String(hour).padStart(2, '0')}:00`) })

  const Item = ({ v }: { v: Rec }) => (
    <span className={`visit-chip socia-${v.socia === 'Leticia' ? 'l' : 'y'} ${v.estado === 'Cancelada' ? 'cancelled' : ''}`}>
      <strong>{fmtTime(v.inicio)}</strong><span>{v.titulo}</span>
    </span>
  )

  return (
    <div className="page">
      <PageHeader eyebrow="Módulo 6" title="Agenda y visitas" subtitle="Visitas, reuniones y firmas. Cada cita queda en DB_Agenda_Visitas."
        actions={<Button variant="primary" icon="plus" onClick={() => newAt(ymd(cursor) < today() ? today() : ymd(cursor))}>Programar visita</Button>} />

      <div className="toolbar">
        <Segmented value={view} onChange={setView} options={[{ value: 'dia', label: 'Día' }, { value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mes' }]} />
        <div className="toolbar-right">
          <div className="legend"><span className="lg lg-l" />Leticia<span className="lg lg-y" />Yesica</div>
          <select id="ag-socia" value={socia} onChange={e => setSocia(e.target.value)} aria-label="Filtrar por socia"><option value="">Ambas socias</option><option>Leticia</option><option>Yesica</option></select>
        </div>
      </div>

      {error && <ErrorNote error={error} onRetry={refresh} />}
      {loading ? <Skeleton rows={4} /> : view === 'mes' ? (
        <>
          <CalNav label={monthLabel(cursor)} onPrev={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} onNext={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} onToday={() => setCursor(new Date())} />
          <MonthGrid month={cursor} items={items} dateOf={dayOf} onItemClick={ed.open} onDayClick={d => newAt(d)} onMove={moveTo} render={v => <Item v={v} />} />
        </>
      ) : view === 'semana' ? (() => {
        const start = startOfWeek(cursor)
        return (
          <>
            <CalNav label={`Semana del ${start.getDate()} de ${MESES[start.getMonth()]}`} onPrev={() => setCursor(addDays(cursor, -7))} onNext={() => setCursor(addDays(cursor, 7))} onToday={() => setCursor(new Date())} />
            <DaysColumns start={start} days={7} items={items} dateOf={dayOf} onItemClick={ed.open} onDayClick={d => newAt(d)} onMove={moveTo}
              render={v => (
                <div className={`visit-card socia-${v.socia === 'Leticia' ? 'l' : 'y'}`}>
                  <span className="vc-time">{fmtTime(v.inicio)} · {v.tipo}</span>
                  <strong>{v.titulo}</strong>
                  <span className="vc-sub">{cli(v.cliente)?.nombre ?? prop(v.propiedad)?.direccion ?? v.lugar}</span>
                  <span className="vc-foot"><Badge>{v.estado}</Badge></span>
                </div>
              )} />
          </>
        )
      })() : (
        <DayView day={ymd(cursor)} items={items.filter(v => dayOf(v) === ymd(cursor))} cli={cli} prop={prop}
          onPrev={() => setCursor(addDays(cursor, -1))} onNext={() => setCursor(addDays(cursor, 1))} onToday={() => setCursor(new Date())}
          onOpen={ed.open} onNew={h => newAt(ymd(cursor), h)} />
      )}

      <RecordForm col="visitas" open={ed.isOpen} record={ed.editing} defaults={ed.creating ?? undefined} onClose={ed.close}
        top={d => <VisitContext d={d} rec={ed.editing} cliente={cli(d.cliente)} propiedad={prop(d.propiedad)} />} />
    </div>
  )
}

function DayView({ day, items, cli, prop, onPrev, onNext, onToday, onOpen, onNew }: { day: string; items: Rec[]; cli: (id: string) => Rec | undefined; prop: (id: string) => Rec | undefined; onPrev: () => void; onNext: () => void; onToday: () => void; onOpen: (r: Rec) => void; onNew: (h: number) => void }) {
  const hours = Array.from({ length: 14 }, (_, i) => i + 8)
  return (
    <>
      <CalNav label={fmtDayLong(day)} onPrev={onPrev} onNext={onNext} onToday={onToday} />
      <div className="dayview">
        {hours.map(h => {
          const list = items.filter(v => new Date(v.inicio).getHours() === h)
          return (
            <div key={h} className="hour">
              <span className="hour-label">{String(h).padStart(2, '0')}:00</span>
              <div className="hour-slot" onClick={e => e.target === e.currentTarget && onNew(h)}>
                {list.map(v => (
                  <button type="button" key={v.id} className={`visit-card wide socia-${v.socia === 'Leticia' ? 'l' : 'y'}`} onClick={() => onOpen(v)}>
                    <span className="vc-time">{fmtTime(v.inicio)} · {v.duracion || 60} min · {v.tipo}</span>
                    <strong>{v.titulo}</strong>
                    <span className="vc-sub">{[cli(v.cliente)?.nombre, prop(v.propiedad)?.direccion || v.lugar].filter(Boolean).join(' · ')}</span>
                    <span className="vc-foot"><Badge>{v.estado}</Badge><Avatar name={v.socia} /></span>
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

export function buildConfirmMessage(d: Record<string, any>, cliente?: Rec, propiedad?: Rec) {
  const nombre = (cliente?.nombre ?? '').replace(/\s*\(ejemplo\)/, '').split(' ')[0] || ''
  const dir = propiedad ? [propiedad.direccion, propiedad.ciudad].filter(Boolean).join(', ') : d.lugar
  const maps = propiedad ? mapsLink(propiedad) : d.lugar ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.lugar)}` : ''
  const fecha = d.inicio ? `${fmtDayLong(d.inicio)} a las ${fmtTime(d.inicio)} h` : '[fecha a confirmar]'
  return [
    `¡Hola${nombre ? ` ${nombre}` : ''}! Te escribo para confirmar ${d.tipo === 'Videollamada' ? 'nuestra videollamada' : 'la visita'}${dir ? ` a ${dir}` : ''}.`,
    '',
    `📅 ${fecha.charAt(0).toUpperCase() + fecha.slice(1)}`,
    maps ? `📍 ${maps}` : '',
    `Te acompaña ${d.socia || 'el equipo'} de REVELA.`,
    '',
    '¿Me confirmás que te queda bien? ✦',
  ].filter((l, i, a) => l !== '' || a[i - 1] !== '').join('\n')
}

function VisitContext({ d, rec, cliente, propiedad }: { d: Record<string, any>; rec: Rec | null; cliente?: Rec; propiedad?: Rec }) {
  const toast = useToast()
  const msg = buildConfirmMessage(d, cliente, propiedad)
  const phone = waNumber(cliente?.whatsapp || cliente?.telefono)
  const maps = propiedad ? mapsLink(propiedad) : ''
  return (
    <div className="context">
      <div className="wa-box">
        <div className="row between"><span className="ctx-label">Confirmar cita por WhatsApp</span>{!phone && <small className="warn-text">El cliente no tiene WhatsApp cargado</small>}</div>
        <pre className="wa-preview">{msg}</pre>
        <div className="row gap-s wrap">
          <a className={`btn btn-accent btn-sm ${!phone ? 'is-disabled' : ''}`} href={phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : undefined} target="_blank" rel="noreferrer" aria-disabled={!phone}>
            <Icon name="chat" size={15} /><span>Confirmar cita</span>
          </a>
          <Button size="sm" variant="quiet" icon="copy" onClick={async () => { const ok = await copyText(msg); toast(ok ? 'Mensaje copiado' : 'No se pudo copiar', ok ? 'ok' : 'error') }}>Copiar mensaje</Button>
          {maps && <a className="btn btn-quiet btn-sm" href={maps} target="_blank" rel="noreferrer"><Icon name="map" size={15} /><span>Google Maps</span></a>}
        </div>
        <small className="field-help">Se abre WhatsApp con el mensaje listo para revisar y enviar. Nada se envía solo.</small>
      </div>
      {rec && (
        <div className="status-row">
          <div><span className="ctx-label">Recordatorio 2 h antes</span><Badge>{rec.recordatorio || 'Desactivado'}</Badge>{rec.recordatorioError && <small className="warn-text block">{rec.recordatorioError}</small>}</div>
          <div><span className="ctx-label">Google Calendar</span>{rec.googleEventId ? <Badge tone="olive">Sincronizada</Badge> : <Badge tone="muted">No conectado</Badge>}</div>
        </div>
      )}
    </div>
  )
}
